/**
 * Storage Hooks
 *
 * React hooks for file picking and upload with progress tracking.
 */

import { useState, useCallback } from 'react';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { uploadFile, deleteFile } from './service';
import type { FilePickResult, UploadOptions, UploadResult, UploadProgress, StorageBucket } from './types';
import { ALLOWED_MIME_TYPES } from './config';

// ============================================================================
// useFilePicker Hook
// ============================================================================

interface UseFilePickerOptions {
  bucket?: StorageBucket;
  multiple?: boolean;
}

interface UseFilePickerReturn {
  pickDocument: () => Promise<FilePickResult | null>;
  pickImage: () => Promise<FilePickResult | null>;
  pickMultiple: () => Promise<FilePickResult[]>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for picking files from the device
 *
 * @example
 * ```typescript
 * const { pickDocument, pickImage, isLoading } = useFilePicker({ bucket: 'dataroom' });
 *
 * const handlePickDocument = async () => {
 *   const file = await pickDocument();
 *   if (file) {
 *     console.log('Picked:', file.name);
 *   }
 * };
 * ```
 */
export function useFilePicker(options: UseFilePickerOptions = {}): UseFilePickerReturn {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const bucket = options.bucket || 'dataroom';

  const pickDocument = useCallback(async (): Promise<FilePickResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ALLOWED_MIME_TYPES[bucket] || ['*/*'],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets?.length) {
        return null;
      }

      const asset = result.assets[0];
      return {
        uri: asset.uri,
        name: asset.name,
        size: asset.size,
        mimeType: asset.mimeType,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pick document';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [bucket]);

  const pickImage = useCallback(async (): Promise<FilePickResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // Request permission
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        setError('Permission to access photos was denied');
        return null;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
        allowsEditing: bucket === 'avatars',
        aspect: bucket === 'avatars' ? [1, 1] : undefined,
      });

      if (result.canceled || !result.assets?.length) {
        return null;
      }

      const asset = result.assets[0];
      const fileName = asset.uri.split('/').pop() || 'image.jpg';

      return {
        uri: asset.uri,
        name: fileName,
        size: asset.fileSize,
        mimeType: asset.mimeType || 'image/jpeg',
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pick image';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [bucket]);

  const pickMultiple = useCallback(async (): Promise<FilePickResult[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ALLOWED_MIME_TYPES[bucket] || ['*/*'],
        copyToCacheDirectory: true,
        multiple: true,
      });

      if (result.canceled || !result.assets?.length) {
        return [];
      }

      return result.assets.map((asset) => ({
        uri: asset.uri,
        name: asset.name,
        size: asset.size,
        mimeType: asset.mimeType,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pick documents';
      setError(message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [bucket]);

  return {
    pickDocument,
    pickImage,
    pickMultiple,
    isLoading,
    error,
  };
}

// ============================================================================
// useFileUpload Hook
// ============================================================================

interface UseFileUploadOptions {
  bucket?: StorageBucket;
  path?: string;
  onSuccess?: (result: UploadResult) => void;
  onError?: (error: string) => void;
}

interface UseFileUploadReturn {
  upload: (file: FilePickResult, options?: Partial<UploadOptions>) => Promise<UploadResult>;
  uploadMultiple: (files: FilePickResult[], options?: Partial<UploadOptions>) => Promise<UploadResult[]>;
  isUploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for uploading files to Supabase Storage with progress tracking
 *
 * @example
 * ```typescript
 * const { upload, isUploading, progress, error } = useFileUpload({
 *   bucket: 'dataroom',
 *   onSuccess: (result) => console.log('Uploaded:', result.publicUrl),
 * });
 *
 * const handleUpload = async (file: FilePickResult) => {
 *   const result = await upload(file);
 *   if (result.success) {
 *     console.log('Success!');
 *   }
 * };
 * ```
 */
export function useFileUpload(options: UseFileUploadOptions = {}): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: FilePickResult, uploadOptions: Partial<UploadOptions> = {}): Promise<UploadResult> => {
      setIsUploading(true);
      setError(null);
      setProgress({ loaded: 0, total: file.size || 0, percentage: 0 });

      try {
        const result = await uploadFile(file, {
          bucket: options.bucket || 'dataroom',
          path: options.path,
          ...uploadOptions,
          onProgress: (p) => {
            setProgress(p);
            uploadOptions.onProgress?.(p);
          },
        });

        if (result.success) {
          options.onSuccess?.(result);
        } else {
          setError(result.error || 'Upload failed');
          options.onError?.(result.error || 'Upload failed');
        }

        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setError(message);
        options.onError?.(message);
        return { success: false, error: message };
      } finally {
        setIsUploading(false);
      }
    },
    [options]
  );

  const uploadMultiple = useCallback(
    async (files: FilePickResult[], uploadOptions: Partial<UploadOptions> = {}): Promise<UploadResult[]> => {
      setIsUploading(true);
      setError(null);

      const results: UploadResult[] = [];
      const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
      let uploadedSize = 0;

      try {
        for (const file of files) {
          const result = await uploadFile(file, {
            bucket: options.bucket || 'dataroom',
            path: options.path,
            ...uploadOptions,
            onProgress: (p) => {
              const overallProgress: UploadProgress = {
                loaded: uploadedSize + p.loaded,
                total: totalSize,
                percentage: Math.round(((uploadedSize + p.loaded) / totalSize) * 100),
              };
              setProgress(overallProgress);
            },
          });

          results.push(result);
          uploadedSize += file.size || 0;

          if (!result.success) {
            setError(result.error || 'Upload failed');
          }
        }

        const allSuccess = results.every((r) => r.success);
        if (allSuccess && results.length > 0) {
          options.onSuccess?.(results[results.length - 1]);
        }

        return results;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setError(message);
        options.onError?.(message);
        return results;
      } finally {
        setIsUploading(false);
      }
    },
    [options]
  );

  const reset = useCallback(() => {
    setIsUploading(false);
    setProgress(null);
    setError(null);
  }, []);

  return {
    upload,
    uploadMultiple,
    isUploading,
    progress,
    error,
    reset,
  };
}

// ============================================================================
// useFileDelete Hook
// ============================================================================

interface UseFileDeleteReturn {
  deleteFile: (bucket: StorageBucket, path: string) => Promise<boolean>;
  isDeleting: boolean;
  error: string | null;
}

/**
 * Hook for deleting files from Supabase Storage
 *
 * @example
 * ```typescript
 * const { deleteFile, isDeleting, error } = useFileDelete();
 *
 * const handleDelete = async () => {
 *   const success = await deleteFile('dataroom', 'path/to/file.pdf');
 *   if (success) {
 *     console.log('File deleted!');
 *   }
 * };
 * ```
 */
export function useFileDelete(): UseFileDeleteReturn {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = useCallback(async (bucket: StorageBucket, path: string): Promise<boolean> => {
    setIsDeleting(true);
    setError(null);

    try {
      const result = await deleteFile(bucket, path);
      if (!result.success) {
        setError(result.error || 'Delete failed');
      }
      return result.success;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      setError(message);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    deleteFile: handleDelete,
    isDeleting,
    error,
  };
}
